import axios from 'axios';
import { AxiosError } from 'axios';
import { ApiError } from '@app/api/ApiError';
import { readToken, deleteRefreshToken, deleteToken, deleteUser, readUser } from '@app/services/localStorage.service';
// import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { saveReturnTo } from '@app/smart-audit/auth/returnTo';

export const httpApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

/** Auth API - base: https://gemba.he.chdt-solution.com:8443/api/auth/v0/oauth2 */
const authBaseURL = process.env.REACT_APP_AUTH_URL || '';

export const httpApiAuth = axios.create({
  baseURL: authBaseURL,
});

/** Auth Domain API - base: https://gemba.he.chdt-solution.com:8443 (for endpoints like /api/auth/v0/users) */
const authDomainURL = authBaseURL ? authBaseURL.replace('/api/auth/v0/oauth2', '') : '';

export const httpApiAuthDomain = axios.create({
  baseURL: authDomainURL,
});

/** Calib API - base: https://gemba.he.chdt-solution.com:8443/calib/api/gemba/v1 */
const calibBaseURL =
  process.env.REACT_APP_CALIB_API_URL ||
  (process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/calib/api/gemba/v1` : '');

export const httpApiCalib = axios.create({
  baseURL: calibBaseURL,
});

/** Kaizen API - base: /api/kaizen/v1 */
const kaizenBaseURL =
  process.env.REACT_APP_KAIZEN_API_URL ||
  (process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/api/kaizen/v1` : '');

export const httpApiKaizen = axios.create({
  baseURL: kaizenBaseURL,
});

/** Smart Audit (5S) API - base: https://gemba.he.chdt-solution.com:8443/smart-audit/api/gemba/v1 */
export const smartAuditBaseURL =
  process.env.REACT_APP_SMART_AUDIT_API_URL ||
  (process.env.REACT_APP_BASE_URL ? `${process.env.REACT_APP_BASE_URL}/smart-audit/api/gemba/v1` : '');

export const httpApiSmartAudit = axios.create({
  baseURL: smartAuditBaseURL,
});

// const navigate = useNavigate();
const requestInterceptor = (config: any) => {
  const token = readToken();
  if (token != undefined && token != '') {
    if (config.url != '/oauth2/login' && config.url != '/login') {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    } else {
      deleteUser();
      deleteToken();
      deleteRefreshToken();
      config.headers = { ...config.headers };
    }
  } else {
    config.headers = { ...config.headers };
  }
  return config;
};

const responseErrorInterceptor = (error: AxiosError) => {
  console.log(error);
  if (!error.response) {
    notificationController.error({
      message: 'Lỗi kết nối tới máy chủ - Liên hệ với quản trị viên | Server Error - Please contact the administrator',
    });
    return Promise.reject(error);
  }

  // If backend already provides a meaningful error message, let the caller decide how to display it.
  // This avoids duplicate notifications (generic + specific).
  const responseData: any = (error.response as any)?.data;
  const backendMessage =
    typeof responseData === 'string'
      ? responseData
      : (responseData?.message as string | undefined) || (responseData?.error as string | undefined);

  if (error.response!.status == 401) {
    if (readUser()) {
      deleteUser();
      deleteToken();
      deleteRefreshToken();
      saveReturnTo(window.location.pathname + window.location.search + window.location.hash);
      window.location.href = '/auth/login';
      notificationController.info({ message: 'Hết phiên đăng nhập | Session has been log out' });
    } else {
      notificationController.error({ message: 'Xác thực thất bại | Authorize failed' });
    }
  } else if (backendMessage && String(backendMessage).trim()) {
    // Don't show generic notification here; screens can show backendMessage explicitly.
  } else {
    notificationController.error({
      message: 'Lỗi kết nối tới máy chủ - Liên hệ với quản trị viên | Server Error - Please contact the administrator',
    });
  }
  return Promise.reject(error);
};

httpApi.interceptors.request.use(requestInterceptor);
httpApi.interceptors.response.use(undefined, responseErrorInterceptor);

httpApiAuth.interceptors.request.use(requestInterceptor);
httpApiAuth.interceptors.response.use(undefined, responseErrorInterceptor);

httpApiAuthDomain.interceptors.request.use(requestInterceptor);
httpApiAuthDomain.interceptors.response.use(undefined, responseErrorInterceptor);

httpApiCalib.interceptors.request.use(requestInterceptor);
httpApiCalib.interceptors.response.use(undefined, responseErrorInterceptor);

httpApiKaizen.interceptors.request.use(requestInterceptor);
httpApiKaizen.interceptors.response.use(undefined, responseErrorInterceptor);

httpApiSmartAudit.interceptors.request.use(requestInterceptor);
httpApiSmartAudit.interceptors.response.use(undefined, responseErrorInterceptor);

export interface ApiErrorData {
  message: string;
}
